/**
 * useTutorLiveSelection — l'état partagé des zones de la salle : l'élève regardé (le filtre du fil, la
 * cible du champ), l'activité ouverte au centre, le profil ouvert par-dessus la salle, le chat.
 *
 * Un réducteur pur (`tutorLiveSelectionReducer`, testé) : changer d'élève referme l'activité d'un autre
 * élève (l'élève regardé fait foi au centre, décision des maquettes du 02/11), le profil et le chat
 * restent ce qu'ils sont.
 */
import { useCallback, useMemo, useReducer } from "react"

import { type TutorLiveSelection } from "./tutorLiveContract"

export type TutorLiveSelectionState = {
    selectedId?: string
    openActivity: { studentId: string; activityId: string } | null
    profileId?: string
    chat: { open: boolean; channel?: string }
}

export type TutorLiveSelectionEvent =
    | { type: "select"; studentId: string | undefined }
    | { type: "open_activity"; studentId: string; activityId: string }
    | { type: "close_activity" }
    | { type: "open_profile"; studentId: string }
    | { type: "close_profile" }
    | { type: "open_chat"; channel?: string }
    | { type: "close_chat" }

export const initialTutorLiveSelection: TutorLiveSelectionState = {
    openActivity: null,
    chat: { open: false },
}

export function tutorLiveSelectionReducer(
    state: TutorLiveSelectionState,
    event: TutorLiveSelectionEvent
): TutorLiveSelectionState {
    switch (event.type) {
        case "select": {
            if (state.selectedId === event.studentId) return state
            const keepActivity = state.openActivity != null && state.openActivity.studentId === event.studentId
            return { ...state, selectedId: event.studentId, openActivity: keepActivity ? state.openActivity : null }
        }
        case "open_activity":
            return {
                ...state,
                selectedId: event.studentId,
                openActivity: { studentId: event.studentId, activityId: event.activityId },
            }
        case "close_activity":
            return state.openActivity == null ? state : { ...state, openActivity: null }
        case "open_profile":
            return { ...state, profileId: event.studentId }
        case "close_profile":
            return state.profileId == null ? state : { ...state, profileId: undefined }
        case "open_chat":
            return { ...state, chat: { open: true, channel: event.channel ?? state.chat.channel } }
        case "close_chat":
            return state.chat.open ? { ...state, chat: { ...state.chat, open: false } } : state
    }
}

export function useTutorLiveSelection(): TutorLiveSelection {
    const [state, dispatch] = useReducer(tutorLiveSelectionReducer, initialTutorLiveSelection)
    const select = useCallback((studentId: string | undefined) => dispatch({ type: "select", studentId }), [])
    const openActivityFor = useCallback(
        (studentId: string, activityId: string) => dispatch({ type: "open_activity", studentId, activityId }),
        []
    )
    const closeActivity = useCallback(() => dispatch({ type: "close_activity" }), [])
    const openProfile = useCallback((studentId: string) => dispatch({ type: "open_profile", studentId }), [])
    const closeProfile = useCallback(() => dispatch({ type: "close_profile" }), [])
    const openChat = useCallback((channel?: string) => dispatch({ type: "open_chat", channel }), [])
    const closeChat = useCallback(() => dispatch({ type: "close_chat" }), [])
    return useMemo(
        () => ({
            ...state,
            select,
            openActivityFor,
            closeActivity,
            openProfile,
            closeProfile,
            openChat,
            closeChat,
        }),
        [state, select, openActivityFor, closeActivity, openProfile, closeProfile, openChat, closeChat]
    )
}
